import React, { useState, useEffect } from 'react';
import { inventoryService } from '../../services/inventoryService';
import './QuickSumModal.css';

const QuickSumModal = ({ isOpen, onClose, appliedFilters, activeStatusTab, activeYearTab }) => {
  const [summaryData, setSummaryData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Auto-generate report when modal opens
  useEffect(() => {
    if (isOpen) {
      generateQuickSum();
    } else {
      // Reset when modal closes
      setSummaryData(null);
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const generateQuickSum = async () => {
    setLoading(true);
    setError(null);

    try {
      // Get all inventory data with current filters applied
      const data = await inventoryService.getInventoryStatus(
        1, // page
        10000, // large limit to get all data
        activeStatusTab,
        activeYearTab,
        appliedFilters
      );

      // Combine results from both tables
      const allInventory = [
        ...(data.inventory2025 || []),
        ...(data.inventoryMain || [])
      ];

      // Generate summary counts
      const summary = {
        totalCount: allInventory.length,
        hairType: {},
        cutByStyle: {},
        invType: {},
        netType: {},
        netSize: {},
        wigLength: {},
        baseColor: {},
        topSize: {},
        warehouse: {},
        wigType: {},
        laceType: {}
      };

      // Count occurrences for each category
      allInventory.forEach(item => {
        // Hair Type
        const hairType = item.Hair_Type || 'Unknown';
        summary.hairType[hairType] = (summary.hairType[hairType] || 0) + 1;

        // Cut By Style
        const cutStyle = item.CutByStyle || 'Unknown';
        summary.cutByStyle[cutStyle] = (summary.cutByStyle[cutStyle] || 0) + 1;

        // Inventory Type
        const invType = item.InventoryType || 'Unknown';
        summary.invType[invType] = (summary.invType[invType] || 0) + 1;

        // Net Type
        const netType = item.Net_Type || 'Unknown';
        summary.netType[netType] = (summary.netType[netType] || 0) + 1;

        // Net Size
        const netSize = item.Net_Size || 'Unknown';
        summary.netSize[netSize] = (summary.netSize[netSize] || 0) + 1;

        // Wig Length
        const wigLength = item.Wig_Length || 'Unknown';
        summary.wigLength[wigLength] = (summary.wigLength[wigLength] || 0) + 1;

        // Base Color
        const baseColor = item.Base_Color || 'Unknown';
        summary.baseColor[baseColor] = (summary.baseColor[baseColor] || 0) + 1;

        // Top Size
        const topSize = item.Top_Size || 'Unknown';
        summary.topSize[topSize] = (summary.topSize[topSize] || 0) + 1;

        // Warehouse
        const warehouse = item.Warehouse || 'Unknown';
        summary.warehouse[warehouse] = (summary.warehouse[warehouse] || 0) + 1;

        // Wig Type
        const wigType = item.Wig_Type || 'Unknown';
        summary.wigType[wigType] = (summary.wigType[wigType] || 0) + 1;

        // Lace Type
        const laceType = item.Lace_Type || 'Unknown';
        summary.laceType[laceType] = (summary.laceType[laceType] || 0) + 1;
      });

      setSummaryData(summary);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const renderCategoryTable = (title, data) => {
    const sortedEntries = Object.entries(data).sort((a, b) => b[1] - a[1]);

    if (summaryData && !summaryData.totalCount && summaryData.totalCount !== 0) {
      setSummaryData(null); // Reset malformed data
      return null;
    }

    return (
      <div className="summary-category">
        <h4>{title}</h4>
        <div className="summary-table">
          {sortedEntries.map(([key, count]) => (
            <div key={key} className="summary-row">
              <span className="summary-label">{key}</span>
              <span className="summary-count">{count}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const printReport = () => {
    if (!summaryData) return;

    const filterLabels = {
      hairLength: 'Hair Length', wigType: 'Wig Type', cutStyle: 'Cut Style',
      baseColor: 'Base Color', inventoryType: 'Inventory Type', netSize: 'Net Size',
      hairType: 'Hair Type', netType: 'Net Type', laceType: 'Lace Type',
      warehouse: 'Warehouse', topSize: 'Top Size'
    };

    const activeFilters = [];
    if (activeYearTab !== 'all') activeFilters.push({ label: 'Year', value: activeYearTab });
    if (activeStatusTab !== 'all') activeFilters.push({ label: 'Status', value: activeStatusTab });
    Object.entries(appliedFilters).forEach(([key, values]) => {
      if (values && values.length > 0)
        activeFilters.push({ label: filterLabels[key] || key, value: values.join(', ') });
    });

    const buildCategoryHtml = (title, data) => {
      const rows = Object.entries(data)
        .sort((a, b) => b[1] - a[1])
        .map(([key, count]) => `
          <tr>
            <td>${key}</td>
            <td class="count">${count}</td>
          </tr>`).join('');
      return `
        <div class="category">
          <table>
            <thead><tr><th colspan="2">${title}</th></tr></thead>
            <tbody>${rows}</tbody>
          </table>
        </div>`;
    };

    const filtersHtml = activeFilters.length === 0
      ? '<span class="filter-item">None — showing all inventory</span>'
      : activeFilters.map(f => `<span class="filter-item"><strong>${f.label}:</strong> ${f.value}</span>`).join('');

    const now = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

    const html = `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Quick Summary Report — ${now}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body { font-family: Arial, sans-serif; font-size: 11px; color: #111; background: #fff; padding: 20px; }

    /* Report Header */
    .report-header { border-bottom: 3px solid #1a1a1a; padding-bottom: 4px; margin-bottom: 6px; display: flex; justify-content: space-between; align-items: flex-end; }
    .report-title { font-size: 18px; font-weight: 700; letter-spacing: 0.5px; color: #1a1a1a; }
    .report-meta { text-align: right; font-size: 10px; color: #555; line-height: 1.6; }

    /* Total Count Banner */
    .total-banner { background: #1a1a1a; color: #fff; padding: 4px 8px; margin-bottom: 6px; display: inline-block; font-size: 14px; font-weight: 600; border-radius: 3px; }

    /* Filters */
    .filters-section { margin-bottom: 14px; padding: 8px 12px; border: 1px solid #ccc; border-radius: 3px; background: #f9f9f9; }
    .filters-label { font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.8px; color: #555; margin-bottom: 6px; }
    .filter-item { display: inline-block; background: #e8e8e8; border: 1px solid #bbb; border-radius: 3px; padding: 2px 8px; margin: 2px 4px 2px 0; font-size: 10px; }
    .filter-item strong { color: #333; }

    /* Category Grid */
    .grid { display: grid; grid-template-columns: repeat(4, 1fr); gap: 10px; }
    .category { break-inside: avoid; }
    table { width: 100%; border-collapse: collapse; border: 1px solid #ccc; }
    thead tr th { color: #000000; padding: 5px 8px; text-align: left; font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px; }
    tbody tr td { padding: 3px 8px; border-bottom: 1px solid #e8e8e8; font-size: 10px; }
    tbody tr:last-child td { border-bottom: none; }
    tbody tr:nth-child(even) td { background: #f5f5f5; }
    td.count { text-align: right; font-weight: 600; color: #1a1a1a; width: 36px; }

    /* Footer */
    .report-footer { margin-top: 18px; border-top: 1px solid #ccc; padding-top: 4px; font-size: 9px; color: #888; display: flex; justify-content: space-between; }

    @media print {
      body { padding: 10px; }
      @page { margin: 12mm; size: landscape; }
    }
  </style>
</head>
<body>
  <div class="report-header">
    <div class="report-title">Inventory Quick Summary Report</div>
    <div class="report-meta">
      <div>Generated: ${now}</div>
      <div>DiNi Tracker — Inventory Management</div>
    </div>
  </div>

  
  <div class="filters-section">
  <div class="filters-label">Applied Filters</div>
  ${filtersHtml}
  <div class="total-banner">Total Wig Count: ${summaryData.totalCount}</div>
  </div>

  <div class="grid">
    ${buildCategoryHtml('Hair Type', summaryData.hairType)}
    ${buildCategoryHtml('Cut By Style', summaryData.cutByStyle)}
    ${buildCategoryHtml('Inv Type', summaryData.invType)}
    ${buildCategoryHtml('Net Type', summaryData.netType)}
    ${buildCategoryHtml('Wig Length', summaryData.wigLength)}
    ${buildCategoryHtml('Base Color', summaryData.baseColor)}
    ${buildCategoryHtml('Top Size', summaryData.topSize)}
    ${buildCategoryHtml('Warehouse', summaryData.warehouse)}
    ${buildCategoryHtml('Wig Type', summaryData.wigType)}
    ${buildCategoryHtml('Lace Type', summaryData.laceType)}
    ${buildCategoryHtml('Net Size', summaryData.netSize)}
  </div>


</body>
</html>`;

    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const win = window.open(url, '_blank');
    win.addEventListener('load', () => {
      win.print();
      URL.revokeObjectURL(url);
    });
  };

  // Helper function to check if filters are active
  const hasActiveFilters = () => {
    return Object.values(appliedFilters).some(filterArray => filterArray && filterArray.length > 0);
  };

  // Format filter display
  const renderAppliedFilters = () => {
    const filterLabels = {
      hairLength: 'Hair Length',
      wigType: 'Wig Type',
      cutStyle: 'Cut Style',
      baseColor: 'Base Color',
      inventoryType: 'Inventory Type',
      netSize: 'Net Size',
      hairType: 'Hair Type',
      netType: 'Net Type',
      laceType: 'Lace Type',
      warehouse: 'Warehouse',
      topSize: 'Top Size'
    };

    const activeFilters = [];

    // Add year filter
    if (activeYearTab !== 'all') {
      activeFilters.push({ label: 'Year', value: activeYearTab });
    }

    // Add status filter
    if (activeStatusTab !== 'all') {
      activeFilters.push({ label: 'Status', value: activeStatusTab });
    }

    // Add applied filters
    Object.entries(appliedFilters).forEach(([key, values]) => {
      if (values && values.length > 0) {
        activeFilters.push({
          label: filterLabels[key] || key,
          value: values.join(', ')
        });
      }
    });

    if (activeFilters.length === 0) {
      return (
        <div className="filters-display">
          <strong>Filters:</strong> None (showing all inventory)
        </div>
      );
    }

    return (
      <div>
        <strong>Applied Filters:</strong>      
          {activeFilters.map((filter, index) => (
            <span key={index} className="filter-badge">
              <strong>{filter.label}:</strong> {filter.value}
            </span>
          ))}
       
      </div>
    );
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="quicksum-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Quick Summary Report</h2>
          <h3>Total Wig Count: {summaryData?.totalCount || 0}</h3>
            {/* Display Applied Filters */}
              {renderAppliedFilters()}
          <button onClick={printReport} className="print-button" title="Print" disabled={!summaryData}>🖨️</button>
          <button onClick={onClose} className="close-button">✕</button>
        </div>

        <div className="modal-content">
          {loading ? (
            <div className="summary-prompt">
              <p>Generating summary report...</p>
              <div className="loading-spinner"></div>
            </div>
          ) : error ? (
            <div className="error-message">
              Error generating summary: {error}
            </div>
          ) : summaryData ? (
            <div className="summary-content">
            

              <div className="summary-grid">
                {/* Left column - stacked short categories */}
                <div className="stacked-categories">
                  {renderCategoryTable('Hair Type', summaryData?.hairType || {})}
                  {renderCategoryTable('CutBy_Style', summaryData?.cutByStyle || {})}
                  {renderCategoryTable('Inv Type', summaryData.invType || {})}
                  {renderCategoryTable('Net_Type', summaryData.netType || {})}
                </div>

                {/* Individual columns for data-heavy categories */}
                {renderCategoryTable('Wig Length', summaryData.wigLength || {})}
                {renderCategoryTable('Base Color', summaryData.baseColor || {})}
                {renderCategoryTable('Top Size', summaryData.topSize || {})}
                {renderCategoryTable('Warehouse', summaryData.warehouse || {})}
                {renderCategoryTable('Wig_Type', summaryData.wigType || {})}
                {renderCategoryTable('Lace Type', summaryData.laceType || {})}
                {renderCategoryTable('Net_Size', summaryData.netSize || {})}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default QuickSumModal;